import * as braze from "@braze/web-sdk";
import type { KadeweBrazeEvent, KadeweUser } from "~/types";

export default defineNuxtPlugin(() => {
    const {
        public: {
            braze: { api_key: apiKey, base_url: baseUrl, logging: enableLogging },
        },
    } = useRuntimeConfig();

    const user = useSanctumUser<KadeweUser>();
    const contentCards = reactive([]) as KadeweBrazeEvent[];

    const startSession = (contactId: string) => {
        console.debug("Braze LP: Starting session");
        console.debug(`Braze LP: Unsubscribing from previous content card updates.`);
        braze.removeAllSubscriptions();

        if (contactId) {
            console.debug(`Braze LP: Changing user to contact ID ${contactId}`);
            braze.changeUser(contactId);
        }
        braze.subscribeToContentCardsUpdates(function (newContentCards) {
            const count = newContentCards.cards.length;
            const unviewedCount = newContentCards.getUnviewedCardCount();
            const time = newContentCards.lastUpdated;
            console.debug(
                `Braze LP: Content cards updated: ${count} cards, ${unviewedCount} unviewed, last updated at ${time}`,
            );

            contentCards.splice(0, contentCards.length, ...(newContentCards.cards as KadeweBrazeEvent[]));
        });

        braze.openSession();
        console.debug(`Braze LP: Session has been created`);
    };

    watch(
        () => user.value?.contact_id,
        (newContactId, oldContactId) => {
            if (newContactId && newContactId !== oldContactId) {
                startSession(newContactId);
            }
        },
    );

    return {
        provide: {
            braze: {
                initialize: () => {
                    if (!apiKey) {
                        console.warn("Braze API key is not set");
                        return;
                    }

                    braze.initialize(apiKey, {
                        baseUrl: baseUrl,
                        enableLogging: enableLogging,
                        openCardsInNewTab: true,
                        localization: user.value?.language,
                    });

                    startSession(user.value?.contact_id ? user.value.contact_id : "");
                },
                logContentCardImpression(event: KadeweBrazeEvent) {
                    console.debug("Braze LP: Logging content card impression", event);
                    braze.logContentCardImpressions([event]);
                },
                logContentCardClick(event: KadeweBrazeEvent) {
                    console.debug("Braze LP: Logging content card click", event);
                    braze.logContentCardClick(event);
                },
                requestContentCardsRefresh: async () => {
                    return new Promise<void>((resolve, reject) => {
                        braze.requestContentCardsRefresh(
                            () => {
                                console.debug("Content cards successfully refreshed.");
                                resolve();
                            },
                            () => {
                                console.error("Failed to refresh content cards!");
                                reject();
                            },
                        );
                    });
                },
                contentCards,
            },
        },
    };
});
